// Onglet « Matériel » de la page du chantier : liste du matériel à commander, calculée par le serveur depuis les lignes du devis
// accepté et leurs ouvrages types, regroupée par fournisseur (tarif de l'entreprise) ou « Fournisseur à choisir », avec l'écart
// avec les pièces déjà posées. Prix et totaux seulement pour les rôles qui voient les coûts (le serveur ne les envoie pas aux
// autres). Export CSV (formules neutralisées) et PDF.
import { useQuery } from '@tanstack/react-query';
import { FileDown, FileText, PackageSearch } from 'lucide-react';
import { Commandes } from './Commandes.tsx';
import { sourceConditionnement } from '../../devis/tarifs.tsx';
import { api } from '../../../lib/api.ts';
import { date, euros, nombre, pluriel } from '../../../lib/format.ts';
import { Alerte, Badge, Chargement, EtatErreur, EtatVide, Panneau } from '../../../components/ui.tsx';

interface LigneMateriel {
  cle: string; reference: string | null; label: string; manufacturer: string | null; unit: string; quantite: string;
  commandee: string; besoin_restant: string; conditionnement: { quantite: string; libelle: string; source: Record<string, unknown> } | null;
  nombre_conditionnements: string | null; quantite_achat: string; surplus: string | null;
  posee: string | null; reste: string | null; sections: string[]; ouvrages: string[]; nb_lignes_devis: number;
  fournisseur: { id: string; nom: string } | null; origine_fournisseur: 'devis' | 'tarif' | null; autres_fournisseurs: number;
  prix_unitaire?: string | null; prix_date?: string | null; total_ht?: string | null;
}
interface Groupe { id: string | null; nom: string; lignes: LigneMateriel[]; total_ht?: string | null; total_incomplet?: boolean }
export interface VueMateriel {
  chantier: { id: string; reference: string | null; title: string }; couts: boolean; sans_devis: boolean; message?: string;
  devis: { number: string; rev_no: number; variant: string } | null;
  fournisseurs: Groupe[];
  totaux: { references: number; lignes_devis: number; deja_posees: number; total_ht?: string | null; total_incomplet?: boolean };
}

const q = (v: string) => nombre(v, 4);
const origine = (l: LigneMateriel) => (l.origine_fournisseur === 'devis' ? 'tarif repris dans le devis'
  : l.origine_fournisseur === 'tarif' ? 'tarif le moins cher de votre bibliothèque' : null);

export function PanneauMateriel({ chantierId }: { chantierId: string }) {
  const r = useQuery({ queryKey: ['chantier', chantierId, 'materiel'], queryFn: () => api.get<VueMateriel>(`/api/chantiers/${chantierId}/materiel`) });
  if (r.isLoading) return <Chargement />;
  if (r.isError) return <EtatErreur erreur={r.error} reessayer={() => r.refetch()} />;
  const v = r.data!;
  const exports = (
    <div className="groupe-btn">
      <a className="btn" href={`/api/chantiers/${chantierId}/materiel.csv`} download><FileDown aria-hidden />Exporter (CSV)</a>
      <a className="btn" href={`/api/chantiers/${chantierId}/materiel.pdf`} target="_blank" rel="noopener"><FileText aria-hidden />PDF</a>
    </div>
  );
  if (v.sans_devis) {
    return <Panneau titre="Matériel à commander"><EtatVide icone={<PackageSearch />} titre="Chantier sans devis" texte={v.message} /></Panneau>;
  }
  return (
    <div className="pile-l">
      <Panneau serre titre="Matériel à commander" actions={v.totaux.references ? exports : undefined}>
        <p className="petit secondaire ch-mat-resume">
          {pluriel(v.totaux.references, 'référence')} tirée{v.totaux.references > 1 ? 's' : ''} {v.devis ? `du devis ${v.devis.number}, révision ${v.devis.rev_no}` : 'du devis'}
          {v.totaux.deja_posees ? ` — ${pluriel(v.totaux.deja_posees, 'référence déjà posée en partie ou en totalité', 'références déjà posées en partie ou en totalité')}` : ''}
          {v.couts && v.totaux.total_ht !== undefined ? <> — fournitures : <strong className="chiffre">{euros(v.totaux.total_ht)}</strong>{v.totaux.total_incomplet ? ' HT, total incomplet (prix à renseigner)' : ' HT'}</> : null}
        </p>
        {!v.totaux.references && <EtatVide icone={<PackageSearch />} titre="Aucune fourniture à commander" texte="Ce devis ne contient que de la main-d’œuvre, des frais ou de la sous-traitance." />}
        {v.fournisseurs.map((g) => (
          <section key={g.id ?? 'a-choisir'} className="ch-mat-groupe" aria-label={g.nom}>
            <h3 className="ch-mat-titre">
              <span>{g.nom}</span>
              <span className="petit discret">{pluriel(g.lignes.length, 'référence')}{v.couts && g.total_ht !== undefined ? ` — ${euros(g.total_ht)} HT${g.total_incomplet ? ' (incomplet)' : ''}` : ''}</span>
            </h3>
            {/* Terrain : une carte par référence, la quantité qui reste à commander en premier. */}
            <ul className="ch-mat-cartes terrain-seulement">
              {g.lignes.map((l) => (
                <li key={l.cle} className="ch-mat-carte">
                  <span className="ch-mat-libelle">{l.label}</span>
                  {l.reference && <span className="mono petit">{l.reference}</span>}
                  <span className="petit">
                    <strong className="chiffre">{q(l.besoin_restant)} {l.unit}</strong> à commander
                    {l.posee !== null && Number(l.posee) > 0 ? ` (sur ${q(l.quantite)}, ${q(l.posee)} déjà posé${Number(l.posee) > 1 ? 's' : ''})` : ''}
                  </span>
                  <span className="petit">Commandé : {q(l.commandee)} {l.unit} — posé : {l.posee === null ? 'Non suivi' : `${q(l.posee)} ${l.unit}`}</span>
                  <DetailConditionnement l={l} />
                  {v.couts && l.total_ht !== undefined && <span className="petit secondaire chiffre">{l.prix_unitaire ? `${euros(l.prix_unitaire)} HT / ${l.unit} — ${euros(l.total_ht)}` : 'Prix à renseigner'}</span>}
                </li>
              ))}
            </ul>
            {/* Expert : tableau complet. */}
            <div className="tableau-conteneur expert-seulement">
              <table className="tableau ch-mat-tableau">
                <thead><tr>
                  <th scope="col">Référence</th><th scope="col">Désignation</th><th scope="col" className="num">Quantité</th><th scope="col" className="num">Déjà posé</th>
                  <th scope="col" className="num">Commandé / reste à commander</th>
                  {v.couts && <><th scope="col" className="num">Prix u. HT</th><th scope="col" className="num">Total HT</th></>}
                </tr></thead>
                <tbody>
                  {g.lignes.map((l) => (
                    <tr key={l.cle}>
                      <td className="mono">{l.reference ?? '—'}</td>
                      <td>
                        {l.label}
                        {(l.manufacturer || l.ouvrages.length > 0 || origine(l) || l.autres_fournisseurs > 0) && (
                          <span className="tres-petit discret ch-mat-detail">
                            {[l.manufacturer, l.ouvrages.length ? `ouvrage : ${l.ouvrages.join(', ')}` : null, origine(l),
                              l.autres_fournisseurs ? pluriel(l.autres_fournisseurs, 'autre fournisseur possible', 'autres fournisseurs possibles') : null].filter(Boolean).join(' — ')}
                          </span>
                        )}
                      </td>
                      <td className="num">{q(l.quantite)} {l.unit}</td>
                      <td className="num">{l.posee === null ? <span className="discret">non suivi</span> : `${q(l.posee)} ${l.unit}`}</td>
                      <td><span>Commandé : {q(l.commandee)} {l.unit}</span><br /><strong>Reste : {q(l.besoin_restant)} {l.unit}</strong><DetailConditionnement l={l} /></td>
                      {v.couts && <>
                        <td className="num">{l.prix_unitaire ? <>{euros(l.prix_unitaire)}{l.prix_date && <span className="tres-petit discret ch-mat-detail">du {date(l.prix_date)}</span>}</> : <Badge ton="ambre" icone={false}>À renseigner</Badge>}</td>
                        <td className="num">{l.total_ht ? euros(l.total_ht) : '—'}</td>
                      </>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </Panneau>
      <Alerte titre="Comment la liste est faite">
        Fournitures du devis accepté (matériel, accessoires, licences, câbles des ouvrages types), consolidées par référence. Fournisseur : celui du
        tarif repris dans le devis, sinon le moins cher des derniers tarifs de votre bibliothèque, sinon « Fournisseur à choisir ». « Déjà posé » :
        pièces posées sur ce chantier avec la même référence. Le reste est le besoin moins le maximum du commandé et du posé, sans double soustraction. Le commandé vient de la dernière version émise de chaque bon ; les brouillons ne comptent pas. Aucun prix n’est inventé ; les prix ne sont visibles que par les rôles qui voient les coûts.
      </Alerte>
      <Commandes materiel={v} />
    </div>
  );
}

function DetailConditionnement({ l }: { l: LigneMateriel }) {
  return <span className="ch-mat-detail petit">{l.conditionnement
    ? <>{l.nombre_conditionnements} × {l.conditionnement.libelle} ({q(l.conditionnement.quantite)} {l.unit})<br />À acheter : {q(l.quantite_achat)} {l.unit}<br />Surplus : {q(l.surplus!)} {l.unit}<span className="ch-mat-detail tres-petit discret">{sourceConditionnement(l.conditionnement.source)}</span></>
    : <>Conditionnement inconnu — quantité brute : {q(l.besoin_restant)} {l.unit}</>}</span>;
}
